import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar, Clock } from 'lucide-react';
import { motion } from 'framer-motion';
import { BLOG_POSTS } from '../data/blogPosts';
import { Title } from '../components/ui/Title';
import { Grid } from '../components/ui/Grid';
import { SEOHead } from '../components/SEOHead';

export default function Blog() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;

  return (
    <>
      <SEOHead
        title={t('blog.title')}
        description={t('blog.description')}
        keywords={['Musik Blog', 'Band finden', 'Musiker Tipps', 'Schweizer Musikszene', 'Live-Musik', 'Band gründen']}
      />
      <div className="container mx-auto px-6 py-8 md:py-24">
        <Title subtitle={t('blog.subtitle')}>
          {t('blog.title')}
        </Title>

        <Grid cols={3}>
          {BLOG_POSTS.map((post, index) => (
            <Link
              key={post.id}
              to={`/blog/${post.id}`}
              className="block w-full h-full"
            >
              <motion.div
                className="panel hover:bg-gray-custom/20 h-full flex flex-col"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
              >
                <motion.div
                  className="aspect-video overflow-hidden rounded-lg mb-4 bg-gray-custom/10"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.2 }}
                >
                  <img
                    src={post.image}
                    alt={t(post.titleKey)}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </motion.div>

                <div className="flex-1 flex flex-col">
                  <motion.div
                    className="flex items-center gap-4 text-sm text-light/60 mb-3"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                  >
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <time dateTime={post.date}>
                        {new Date(post.date).toLocaleDateString(currentLang, {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </time>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>{post.readTime} {t('blog.minRead')}</span>
                    </div>
                  </motion.div>

                  <motion.h2
                    className="font-tilt-warp text-xl text-light mb-3"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                  >
                    {t(post.titleKey)}
                  </motion.h2>

                  <motion.p
                    className="text-light/60 line-clamp-3 mb-4 flex-1"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                  >
                    {t(post.excerptKey)}
                  </motion.p>

                  <motion.div
                    className="text-primary font-semibold"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                  >
                    {t('blog.readMore')} →
                  </motion.div>
                </div>
              </motion.div>
            </Link>
          ))}
        </Grid>
      </div>
    </>
  );
}
